/**
 * Lightweight client-side spam score checker for email campaigns.
 * Heuristic-based — no external API. Returns a score (0-100) and a list of issues.
 *
 *   0–29  → low risk (green)
 *  30–59  → medium risk (amber)
 *  60–100 → high risk (red)
 */

// Common Spanish + English spam trigger words.
const SPAM_KEYWORDS = [
  'gratis', 'free', '100%', 'urgente', 'urgent', 'oferta limitada', 'última oportunidad',
  'ganaste', 'ganador', 'winner', 'haz click aquí', 'click aquí', 'click here',
  'compra ahora', 'buy now', 'increíble', 'increible', 'sin costo',
  'oferta especial', 'descuento exclusivo', 'aprovecha ya', 'no pierdas',
  'casino', 'apuesta', 'préstamo', 'prestamo', 'crédito fácil', 'credito facil',
  'viagra', 'viagrá', 'pastilla', 'milagro', 'milagroso',
  'felicidades', 'has sido seleccionado', 'premio', 'sorteo',
  'dinero rápido', 'dinero rapido', 'gana dinero',
];

const EMOJI_REGEX = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu;

const stripHtml = (html = '') => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * @param {{subject: string, body: string}} campaign
 * @returns {{score: number, level: 'low'|'medium'|'high', issues: Array<{severity:'info'|'warn'|'error', message:string, points:number}>}}
 */
export const checkSpamScore = ({ subject = '', body = '' } = {}) => {
  const issues = [];
  let score = 0;

  const subjLower = subject.toLowerCase();
  const bodyText = stripHtml(body);
  const bodyLower = bodyText.toLowerCase();

  // 1. Spam keywords
  const foundKeywords = SPAM_KEYWORDS.filter(kw => subjLower.includes(kw) || bodyLower.includes(kw));
  if (foundKeywords.length > 0) {
    const pts = Math.min(30, foundKeywords.length * 10);
    score += pts;
    issues.push({
      severity: foundKeywords.length >= 3 ? 'error' : 'warn',
      message: `Palabras red-flag detectadas: ${foundKeywords.slice(0, 4).join(', ')}${foundKeywords.length > 4 ? '…' : ''}`,
      points: pts,
    });
  }

  // 2. Excessive exclamation marks
  const exclamCount = (subject.match(/!/g) || []).length;
  if (exclamCount >= 3) {
    score += 15;
    issues.push({ severity: 'error', message: `${exclamCount} signos "!" en el asunto — los filtros lo marcan como sensacionalista`, points: 15 });
  } else if (exclamCount === 2) {
    score += 7;
    issues.push({ severity: 'warn', message: 'Dos "!" en el asunto — preferí uno solo', points: 7 });
  }

  // 3. ALL CAPS in subject (>50% letters)
  const letters = subject.replace(/[^A-Za-zÁÉÍÓÚÑáéíóúñ]/g, '');
  if (letters.length >= 6) {
    const upper = letters.replace(/[a-záéíóúñ]/g, '').length;
    const ratio = upper / letters.length;
    if (ratio > 0.7) {
      score += 20;
      issues.push({ severity: 'error', message: 'Asunto casi todo en MAYÚSCULAS — penaliza fuerte la entrega', points: 20 });
    } else if (ratio > 0.5) {
      score += 10;
      issues.push({ severity: 'warn', message: 'Muchas mayúsculas en el asunto — bajalo un poco', points: 10 });
    }
  }

  // 4. Too many emojis in subject
  const emojiCount = (subject.match(EMOJI_REGEX) || []).length;
  if (emojiCount >= 4) {
    score += 12;
    issues.push({ severity: 'error', message: `${emojiCount} emojis en el asunto — máx 1 o 2 recomendado`, points: 12 });
  } else if (emojiCount === 3) {
    score += 5;
    issues.push({ severity: 'warn', message: '3 emojis en el asunto — está al límite', points: 5 });
  }

  // 5. Subject length
  if (subject.length === 0) {
    score += 20;
    issues.push({ severity: 'error', message: 'Asunto vacío', points: 20 });
  } else if (subject.length < 10) {
    score += 8;
    issues.push({ severity: 'warn', message: `Asunto muy corto (${subject.length} chars) — apuntá a 30-60`, points: 8 });
  } else if (subject.length > 78) {
    score += 8;
    issues.push({ severity: 'warn', message: `Asunto muy largo (${subject.length} chars) — se corta en mobile`, points: 8 });
  }

  // 6. Body too short / empty
  if (bodyText.length === 0) {
    score += 30;
    issues.push({ severity: 'error', message: 'Cuerpo vacío', points: 30 });
  } else if (bodyText.length < 50) {
    score += 12;
    issues.push({ severity: 'warn', message: `Cuerpo muy corto (${bodyText.length} chars) — los filtros desconfían`, points: 12 });
  }

  // 7. Money symbols / "$$$"
  if (/\${2,}/.test(subject + body) || /€{2,}/.test(subject + body)) {
    score += 12;
    issues.push({ severity: 'warn', message: 'Símbolos de dinero repetidos ($$$, €€€) son red-flag', points: 12 });
  }

  // 8. Multiple "??"
  if (/\?{2,}/.test(subject)) {
    score += 8;
    issues.push({ severity: 'warn', message: 'Signos "??" repetidos en el asunto', points: 8 });
  }

  // 9. Click-bait phrases ("haz click", "click aquí", etc.) — already in keywords list

  // 10. Missing unsubscribe / contact hint (informational only)
  if (bodyText.length > 80 && !/baja|unsubscribe|cancelar|dar de baja/i.test(bodyText)) {
    issues.push({ severity: 'info', message: 'Tip: incluí un enlace de baja en el cuerpo (mejora la confianza)', points: 0 });
  }

  // Cap score at 100
  score = Math.min(100, score);

  let level;
  if (score >= 60) level = 'high';
  else if (score >= 30) level = 'medium';
  else level = 'low';

  return { score, level, issues };
};
